import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Play, Youtube, ArrowRight } from "lucide-react";
import { VIDEOS, YT_CHANNEL_URL } from "@/lib/videos";

// A single video card. Loads a high-quality YouTube thumbnail up front
// and only swaps in the real iframe once the card scrolls into view,
// so the page stays fast even with 8+ videos on it.
const VideoCard = ({ video, index }) => {
  const { id, title, kind = "short" } = video;
  const cardRef = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = cardRef.current;
    if (!el || inView) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setInView(true);
            obs.disconnect();
          }
        });
      },
      { threshold: 0.4 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [inView]);

  const aspect = kind === "short" ? "aspect-[9/16]" : "aspect-video";
  const embedSrc = `https://www.youtube-nocookie.com/embed/${id}?autoplay=1&mute=1&loop=1&playlist=${id}&controls=0&modestbranding=1&playsinline=1&rel=0`;
  const thumb = `https://i.ytimg.com/vi/${id}/hqdefault.jpg`;

  return (
    <motion.a
      ref={cardRef}
      href={`https://www.youtube.com/watch?v=${id}`}
      target="_blank"
      rel="noopener noreferrer"
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.4, delay: Math.min(index * 0.06, 0.4) }}
      data-testid={`video-card-${index}`}
      className={`group relative block ${aspect} bg-[#07111C] border border-slate-800 rounded-md overflow-hidden hover:border-[#1E88E5]/70 transition-colors`}
    >
      {inView ? (
        <iframe
          src={embedSrc}
          title={title}
          loading="lazy"
          allow="autoplay; encrypted-media; picture-in-picture"
          referrerPolicy="strict-origin-when-cross-origin"
          className="absolute inset-0 w-full h-full pointer-events-none"
        />
      ) : (
        <img
          src={thumb}
          alt={title}
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity"
        />
      )}

      {/* Hover overlay — play icon + "Watch on YouTube" */}
      <div className="absolute inset-0 bg-gradient-to-t from-[#07111C]/95 via-[#07111C]/20 to-transparent pointer-events-none" />

      <div className="absolute top-3 right-3 bg-[#0B1726]/80 backdrop-blur-sm border border-slate-800 rounded-sm w-8 h-8 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
        <Play className="w-3.5 h-3.5 text-white fill-white" />
      </div>

      <div className="absolute bottom-0 inset-x-0 p-4 pointer-events-none">
        <div className="font-heading text-xs font-bold tracking-[0.25em] uppercase text-[#E6B445] mb-1">
          Short
        </div>
        <div className="font-heading text-sm sm:text-base font-bold text-white leading-tight line-clamp-2">
          {title}
        </div>
      </div>
    </motion.a>
  );
};

export const YouTubeVideos = () => {
  // If the owner hasn't curated videos yet, don't render the section at all
  // rather than showing an empty shell.
  if (!VIDEOS || VIDEOS.length === 0) return null;

  return (
    <section
      id="videos"
      className="relative py-24 sm:py-32 bg-[#07111C] border-y border-slate-900"
      data-testid="videos-section"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <span className="h-px w-10 bg-[#1E88E5]" />
              <span className="font-heading text-xs font-bold tracking-[0.3em] uppercase text-[#E6B445] flex items-center gap-2">
                <Youtube className="w-4 h-4" /> Watch us work
              </span>
            </div>
            <h2 className="font-heading text-4xl sm:text-6xl font-black uppercase tracking-tight text-white">
              Real jobs. <br />
              <span className="text-[#1E88E5]">Real results.</span>
            </h2>
          </div>
          <a
            href={YT_CHANNEL_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="videos-channel-link"
            className="self-start md:self-end inline-flex items-center gap-2 font-heading uppercase text-sm tracking-wider text-slate-300 hover:text-white transition-colors"
          >
            Visit our YouTube
            <ArrowRight className="w-4 h-4" />
          </a>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
          {VIDEOS.map((v, i) => (
            <VideoCard key={v.id} video={v} index={i} />
          ))}
        </div>

        <p className="text-slate-500 text-sm mt-8">
          Videos play muted. Tap any tile to watch on YouTube with sound.
        </p>
      </div>
    </section>
  );
};
